import React from "react";
import {
  FaDumbbell,
  FaEdit,
  FaExternalLinkAlt,
  FaShareAlt,
  FaTrash,
} from "react-icons/fa";

function ExerciseCatalog({
  exercises,
  editingExercise,
  selectedExerciseForAssign,
  deletingIds = [],
  onEdit,
  onDelete,
  onAssign,
}) {
  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-200">
            Catalogue
          </p>

          <h2 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
            Exercices disponibles
          </h2>
        </div>

        <p className="text-sm font-medium text-slate-500 dark:text-white/50">
          {exercises.length} exercice{exercises.length > 1 ? "s" : ""}
        </p>
      </div>

      {exercises.length === 0 ? (
        <div className="mt-6 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-white/10 dark:bg-white/[0.03]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-500 shadow-sm dark:bg-white/10 dark:text-white/50">
            <FaDumbbell className="text-2xl" />
          </div>

          <p className="mt-4 text-sm font-bold text-slate-700 dark:text-white">
            Aucun exercice créé.
          </p>

          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
            Crée ton premier exercice depuis le formulaire de droite.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {exercises.map((exercise) => {
            const isDeleting = deletingIds.includes(exercise.id);
            const isEditing = editingExercise?.id === exercise.id;
            const isSelectedForAssign =
              selectedExerciseForAssign?.id === exercise.id;

            return (
              <article
                key={exercise.id}
                className={`rounded-[1.75rem] border p-4 transition ${
                  isSelectedForAssign
                    ? "border-emerald-300 bg-emerald-50 shadow-lg shadow-emerald-500/10 dark:border-emerald-400/30 dark:bg-emerald-500/10"
                    : isEditing
                      ? "border-sky-300 bg-sky-50 shadow-lg shadow-sky-500/10 dark:border-sky-400/30 dark:bg-sky-500/10"
                      : "border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/[0.04]"
                }`}
              >
                <div className="flex flex-col gap-4 sm:flex-row">
                  <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-[1.5rem] bg-white shadow-sm dark:bg-slate-950 sm:w-36">
                    {exercise.lien_photo ? (
                      <img
                        src={exercise.lien_photo}
                        alt={exercise.nom}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FaDumbbell className="text-3xl text-slate-400" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-2">
                      {isSelectedForAssign && (
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                          Sélectionné pour attribution
                        </span>
                      )}

                      {isEditing && (
                        <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                          En modification
                        </span>
                      )}
                    </div>

                    <h3 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                      {exercise.nom}
                    </h3>

                    <p className="mt-2 line-clamp-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
                      {exercise.description || "Aucune description renseignée."}
                    </p>

                    {exercise.programme && (
                      <p className="mt-3 rounded-2xl bg-white px-3 py-2 text-xs font-bold text-emerald-700 shadow-sm dark:bg-white/10 dark:text-emerald-200">
                        {exercise.programme}
                      </p>
                    )}

                    {exercise.lien_video && (
                      <a
                        href={exercise.lien_video}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-sky-700 hover:text-sky-900 dark:text-sky-200"
                      >
                        <FaExternalLinkAlt />
                        Voir la vidéo
                      </a>
                    )}
                  </div>

                  <div className="grid gap-2 sm:w-36">
                    <button
                      type="button"
                      onClick={() => onAssign(exercise)}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5"
                    >
                      <FaShareAlt />
                      Attribuer
                    </button>

                    <button
                      type="button"
                      onClick={() => onEdit(exercise)}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5"
                    >
                      <FaEdit />
                      Modifier
                    </button>

                    <button
                      type="button"
                      onClick={() => onDelete(exercise)}
                      disabled={isDeleting}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
                    >
                      <FaTrash />
                      {isDeleting ? "Suppression..." : "Supprimer"}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default ExerciseCatalog;